import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Col, Icon, Empty, message } from 'antd';
import { makeStyles } from '@material-ui/styles';
import classNames from 'classnames';
import {
  PropertyOutline as PropertyIcon,
  ArrowRight as ArrowRightIcon,
} from 'common/components/svgs';
import { dayFormatType, formatDay } from 'common/helpers/day';
import { useHistory } from 'react-router-dom';
import formatPrice from '~helpers/currency';
import { OrderActionField } from '~pages/longtail-bookings/list/booking-table';
import CounterOfferModal from '~pages/longtail-bookings/counter-offer-modal';
import { useLazyQuery } from '@apollo/client';
import { orderStage, orderSecondaryStage } from 'common/constants/booking';
import generatePath from '~settings/routing';
import { QUERY_DIFFERENT_STAGE_ORDERS } from './queries';
import { pageType } from '~constants/pages';

const useStyles = makeStyles(theme => ({
  loading: {
    height: '80vh',
  },
  money: {
    color: '#FF4D4F',
    ...theme.typography.h5,
  },
  pendingBoxHint: {
    background: '#FF4D4F',
    ...theme.typography.body5,
    padding: '0px 6px',
    borderRadius: 10,
    color: theme.colors.gray1,
  },

  pendingBoxTitle: {
    ...theme.typography.h5,
    marginRight: 8,
    color: theme.colors.titleColor,
  },
  row: {
    background: theme.colors.gray1,
    marginTop: 16,
    paddingLeft: 24,
    paddingRight: 24,
    paddingBottom: 24,
    '& .ant-empty-normal': {
      margin: '64px 0',
    },
  },
  more: {
    paddingBottom: 0,
    paddingLeft: 0,
    paddingRight: 0,
  },
  circleIcon: {
    width: 6,
    height: 6,
    background: '#FA8C16',
    display: 'inline-block',
    marginRight: 8,
    borderRadius: '50%',
    verticalAlign: 2,
  },
  pendingIcon: {
    color: theme.colors.blue6,
    marginRight: 8,
  },

  scrollBox: {
    width: '100%',
    overflow: 'scroll',
    display: 'flex',
    paddingTop: 18,
    paddingLeft: 24,
    paddingBottom: 24,
    position: 'relative',
  },

  operate: {
    background: theme.colors.gray1,
    width: 320,
    flex: 'none',
    border: '1px solid rgba(0, 0, 0, 0.06)',
    marginRight: 16,
    borderRadius: 2,
    cursor: 'pointer',
    '&:hover': {
      transition: 'all 0.5s ease',
      boxShadow:
        ' 0px 3px 6px -4px rgba(0, 0, 0, 0.12), 0px 6px 16px rgba(0, 0, 0, 0.08), 0px 9px 28px 8px rgba(0, 0, 0, 0.05)',
    },
  },

  operateInfo: {
    padding: '24px 32px 32px',
  },

  operateLabel: {
    background: theme.colors.gray3,
    color: theme.colors.captionColor,
    padding: '4px 8px',
    ...theme.typography.body5,
  },

  studentName: {
    ...theme.typography.h4,
    marginTop: 16,
    marginBottom: 12,
    color: theme.colors.titleColor,
  },

  leftIcon: {
    width: 14,
    marginRight: 8,
    color: theme.colors.captionColor,
  },
  middleIcon: {
    verticalAlign: -3,
  },
  rightWording: {
    ...theme.typography.body2,
    color: theme.colors.titleColor,
    fontWeight: 400,
    marginTop: 8,
    marginRight: 8,
  },
  scrollBoxSupport: {
    position: 'absolute',
    width: 40,
    right: 0,
    top: 0,
    bottom: 0,
    background: ' linear-gradient(270deg, #FFFFFF 0%, rgba(255, 255, 255, 0) 89.56%)',
  },
  editBox: {
    background: theme.colors.gray2,
    height: 54,
    textAlign: 'center',
    paddingTop: 16,
    '& > div:first-child': {
      justifyContent: 'center',
    },

    '& .action-divider': {
      marginRight: 12,
      marginLeft: 12,
    },
    '& .accept-modal__btn, .counterBtn': {
      '&:hover': {
        color: theme.colors.mainGreen5,
      },
      '&:active': {
        color: theme.colors.mainGreen7,
      },
    },
    '& .reject-modal__btn': {
      '&:hover': {
        color: '#ff4d4f',
      },
      '&:active': {
        color: '#cf1322',
      },
    },
    '& .edit-modal__btn': {
      marginRight: 0,
      '&:hover': {
        color: '#5abfb2',
      },
      '&:active': {
        color: '#248c85',
      },
    },
  },
  totalColor: {
    color: theme.colors.bodyColor,
    marginTop: 20,
  },
  roomWording: {
    ...theme.typography.body4,
    color: theme.colors.captionColor,
    fontWeight: 400,
  },
  flexLine: {
    ...theme.flex.crossCenter,
    marginBottom: 8,
  },
  desc: {
    ...theme.typography.body2,
    color: theme.colors.titleColor,
    fontWeight: 400,
    width: 220,
    ...theme.singleLineOverflow,
  },
  descUnit: {
    ...theme.typography.body2,
    color: theme.colors.titleColor,
    fontWeight: 400,
    maxWidth: 120,
    marginRight: 8,
    ...theme.singleLineOverflow,
  },
  title: {
    paddingTop: 17,
    paddingBottom: 17,
  },
  less: {
    paddingBottom: 0,
    paddingLeft: 24,
  },
}));

const isShowCounterOffer = order => {
  const isPendingCounterOffer = order?.secondaryStage === orderSecondaryStage.PENDING_COUNTER_OFFER;

  const isOverTimeCounterOffer =
    order.secondaryStage === orderSecondaryStage.COUNTER_OFFER_NON_CONFIRM_IN_TIME;

  const isRejectAll =
    order.secondaryStage === orderSecondaryStage.REJECT_BY_LANDLORD &&
    order.stage === orderStage.LOST &&
    !!order?.counterOffers?.length;
  return isPendingCounterOffer || isOverTimeCounterOffer || isRejectAll;
};
const PendingConfirmation = ({ order, refetchData }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const classes = useStyles();
  const [orderInfo, setOrderInfo] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const sortOrder = data => {
    const order = data
      ?.map(i => i.node)
      ?.map(i => {
        let sortValue = i?.secondaryStage === orderSecondaryStage.PENDING_COUNTER_OFFER ? 1 : 0;
        return { ...i, sortValue };
      });
    order?.sort((a, b) => {
      return a.sortValue - b.sortValue;
    });
    return order;
  };
  const formatOrder = data => {
    const { totalCount, edges, pageInfo } = data;

    const orders = sortOrder(edges);
    setOrderInfo(orders);
    setTotalCount(totalCount);
    setCurrentPage(currentPage + 1);
    setHasNextPage(pageInfo?.hasNextPage);
  };

  useEffect(() => {
    formatOrder(order);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order]);

  const counterOfferModalRef = useRef(null);
  const openOrderCounterOfferModal = row => {
    counterOfferModalRef.current?.handleOpenModal(row);
  };
  const [queryOrder] = useLazyQuery(QUERY_DIFFERENT_STAGE_ORDERS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    onError: e => {
      message.error(e.toString());
    },
    onCompleted: result => {
      if (result?.viewer?.company?.orders) {
        handleOrder(result?.viewer?.company?.orders);
      }
    },
  });
  const handleOrder = data => {
    const { edges, pageInfo } = data;

    const ordersContent = sortOrder(edges);

    const newOrderInfo = orderInfo.concat(ordersContent);

    setOrderInfo(newOrderInfo);
    setCurrentPage(currentPage + 1);
    setHasNextPage(pageInfo.hasNextPage);
  };
  let scrollFinished = true;
  const loadData = () => {
    scrollFinished = true;

    const variables = {
      pageNumber: currentPage,
      stages: 'WAITING_FOR_CONFIRMATION',
      pageSize: 10,
    };
    queryOrder({ variables });
  };

  const handleScroll = () => {
    const scrollEle = document.getElementById('pendingConfirmContent');
    const clientWidth = scrollEle.clientWidth;
    const scrollLeft = scrollEle.scrollLeft;
    const scrollWidth = scrollEle.scrollWidth;
    if (scrollFinished && clientWidth + scrollLeft >= scrollWidth) {
      scrollFinished = false;
      if (hasNextPage) {
        loadData();
      }
    }
  };

  const handleMoveIndata = row => {
    let moveInDate = isShowCounterOffer(row) ? row.counterOffers?.[0]?.moveInDate : row?.moveInDate;
    return moveInDate ? formatDay(dayFormatType.MDY, moveInDate) : '-';
  };

  const handleMoveOutdate = row => {
    let moveOutDate = isShowCounterOffer(row)
      ? row.counterOffers?.[0]?.moveOutDate
      : row?.moveOutDate;
    return moveOutDate ? formatDay(dayFormatType.MDY, moveOutDate) : '-';
  };

  const handlePropertyName = row => {
    let propertyName = isShowCounterOffer(row)
      ? row.counterOffers?.[0]?.property?.name
      : row?.property?.name;
    return propertyName;
  };

  const handleTotalPrice = row => {
    const totalPrice = isShowCounterOffer(row)
      ? row.counterOffers?.[0]?.totalPrice
      : row?.totalPrice;
    const currency = isShowCounterOffer(row) ? row.counterOffers?.[0]?.currency : row?.currency;
    return formatPrice(totalPrice, currency);
  };

  useEffect(() => {
    const scrollEle = document.getElementById('pendingConfirmContent');
    if (scrollEle) {
      scrollEle.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (scrollEle) {
        scrollEle.removeEventListener('scroll', handleScroll);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!totalCount, orderInfo, hasNextPage]);
  const openOrderDetailsPage = record => {
    history.push(
      generatePath(
        'orders.detail',
        { id: record?.referenceId },
        { from: 'Dashboard|Pending Confirmation' },
      ),
    );
  };
  return (
    <Row className={classNames(classes.row, { [classes.more]: !!totalCount })}>
      <Col>
        <div className={classNames(classes.title, { [classes.less]: !!totalCount })}>
          <Icon type="clock-circle" theme="filled" className={classes.pendingIcon} />

          <span className={classes.pendingBoxTitle}>
            {t('landlord.long_tail.booking.list.pending_confirmation')}
          </span>
          <If condition={totalCount}>
            <span className={classes.pendingBoxHint}>{totalCount}</span>
          </If>
        </div>
      </Col>
      <If condition={!!totalCount && !!orderInfo?.length}>
        <Col>
          <div>
            <div className={classes.scrollBox} id="pendingConfirmContent">
              <For of={orderInfo} each="item" index="index">
                <div
                  className={classes.operate}
                  onClick={e => {
                    e.stopPropagation();
                    e.preventDefault();

                    openOrderDetailsPage(item);
                  }}
                >
                  <div className={classes.operateInfo}>
                    <label className={classes.operateLabel}>{item?.referenceId}</label>
                    <div
                      className={classes.studentName}
                    >{`${item?.student?.passportFirstName}  ${item?.student?.passportLastName}`}</div>
                    <div className={classes.flexLine}>
                      <PropertyIcon className={classes.leftIcon} />
                      <span className={classes.desc}>{handlePropertyName(item)}</span>
                    </div>
                    <div className={classes.flexLine}>
                      <Icon type="home" className={classes.leftIcon} />
                      <If condition={!!item?.counterOffers?.length}>
                        <span className={classes.descUnit}>
                          {item?.counterOffers?.[0]?.unitTypeName}
                        </span>
                      </If>
                      <If condition={!item?.counterOffers?.length}>
                        <span className={classes.descUnit}>{item?.unitTypeName}</span>
                      </If>

                      <span className={classes.roomWording}>
                        <If condition={!!item?.counterOffers?.length}>
                          (
                          {t(
                            `landlord.long_tail.booking.unit_type_category.${
                              item?.counterOffers?.[0]?.unitTypeCategory
                                ? item.counterOffers[0].unitTypeCategory.toLowerCase()
                                : ''
                            }`,
                          )}
                          )
                        </If>
                        <If condition={!item?.counterOffers?.length}>
                          (
                          {t(
                            `landlord.long_tail.booking.unit_type_category.${
                              item?.unitTypeCategory ? item.unitTypeCategory.toLowerCase() : ''
                            }`,
                          )}
                          )
                        </If>
                      </span>
                    </div>
                    <div>
                      <Icon type="calendar" className={classes.leftIcon} />
                      <span className={classes.rightWording}>{handleMoveIndata(item)}</span>
                      <ArrowRightIcon
                        className={classNames(classes.leftIcon, classes.middleIcon)}
                      />
                      <span className={classes.rightWording}>{handleMoveOutdate(item)}</span>
                    </div>
                    <div className={`${classes.rightWording} ${classes.totalColor}`}>
                      {t('landlord.long_tail.booking_details.label.total_price')}:{' '}
                      <span className={classes.money}>{handleTotalPrice(item)}</span>
                    </div>
                  </div>
                  <div className={classes.editBox}>
                    <OrderActionField
                      stage={item?.stage}
                      bookingId={item?.id}
                      refetchData={refetchData}
                      record={item}
                      isHerculesBooking={true}
                      from={pageType.dashboard}
                      openOrderCounterOfferModal={openOrderCounterOfferModal}
                    />
                  </div>
                </div>
              </For>
              <CounterOfferModal
                wrappedComponentRef={counterOfferModalRef}
                refetchBookingList={refetchData}
                from={pageType.dashboard}
              />
            </div>
            <div className={classes.scrollBoxSupport} />
          </div>
        </Col>
      </If>
      <If condition={!totalCount}>
        <Col>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('board.empty.pending_confirmation.dec')}
          />
        </Col>
      </If>
    </Row>
  );
};
export default PendingConfirmation;
